import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, TextField, InputAdornment,
} from '@mui/material';
import { useTranslation } from 'src/i18n';

/**
 * Count/verdict rows as a table: [label, count, question, yes/no/na].
 *
 * Visually a sibling of CheckSheet, but it cannot reuse it — CheckSheet renders
 * check_sheet_tasks rows from redux and stores { task_id, status, comment }, whereas these
 * rows are extras jsonb holding { count, verdict } at an arbitrary path. Shared markup
 * conventions (MUI Table, main-headers, checkbox cells) rather than shared code.
 *
 * A row with no `countKey` renders a blank count cell — the panel deficiency questions have
 * a verdict but nothing to count.
 */
export default function CountVerdictTable (props) {
  const { title, labelHeader, rows, data, extrasPath, onChange, readOnly, unit, footnote } = props;
  const { t } = useTranslation();

  const statuses = ['pass', 'fail', 'na'];
  const statusLabels = { pass: t('common.yes'), fail: t('common.no'), na: t('common.na') };

  const verdictOf = (row) => data?.getIn([row.key, 'verdict']) ?? data?.get(row.key);
  const countOf = (row) => data?.getIn([row.key, 'count']) ?? '';

  // Clicking the checked box clears it, matching CheckSheet's toggle behaviour.
  const setVerdict = (row, status) => {
    const next = verdictOf(row) === status ? null : status;
    onChange([...extrasPath, row.key, ...(row.hasCount ? ['verdict'] : [])], next);
  };

  return (
    <Paper className='check-sheet-paper count-verdict-table'>
      <h3>{title}</h3>
      <TableContainer className='check-sheet-table-container'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className='main-headers'>{labelHeader}</TableCell>
              <TableCell className='main-headers' align='center'>{t('hmsAudit.table.count')}</TableCell>
              <TableCell className='main-headers'>{t('hmsAudit.table.question')}</TableCell>
              {statuses.map(status => (
                <TableCell key={status} className='main-headers' align='center' size='small'>
                  {statusLabels[status]}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.key} className='checkbox-rows'>
                <TableCell className='count-verdict-label'>{row.label}</TableCell>
                <TableCell align='center' className='count-verdict-count'>
                  {row.hasCount && (
                    <TextField
                      type='number'
                      value={countOf(row)}
                      disabled={readOnly}
                      onChange={(e) => onChange([...extrasPath, row.key, 'count'], e.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> }}
                    />
                  )}
                </TableCell>
                <TableCell className='count-verdict-question-cell'>{row.question}</TableCell>
                {statuses.map(status => (
                  <TableCell key={status} size='small' padding='checkbox'>
                    {row.hasVerdict !== false && (
                      <Checkbox
                        checked={verdictOf(row) === status}
                        disabled={readOnly}
                        onChange={() => setVerdict(row, status)}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {footnote && <p className='count-verdict-footnote'><i>{footnote}</i></p>}
    </Paper>
  );
}

CountVerdictTable.propTypes = {
  title: PropTypes.string.isRequired,
  labelHeader: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    question: PropTypes.string,
    // False for rows that only ask a question, e.g. the panel deficiency checks.
    hasCount: PropTypes.bool,
    // False for rows that only report a count, e.g. failed IR tests.
    hasVerdict: PropTypes.bool,
  })).isRequired,
  data: ImmutablePropTypes.map,
  // Extras path to the object holding these rows, e.g. ['extras','panelInspection'].
  extrasPath: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  unit: PropTypes.string,
  // Explanatory note rendered under the table, e.g. what an operational test covers.
  footnote: PropTypes.string,
};

CountVerdictTable.defaultProps = {
  readOnly: false,
  unit: 'EA',
};
